import { useCallback, useEffect, useRef, useState } from "react";
import { fetchBranches, listBuilds, generateBuild, getBuildStatus, buildDownloadUrl, type BuildItem } from "../api/builds";
import { absoluteTime, isoTime, relativeAge } from "../lib/time";

type Props = {
  project: string;
  /** Display name for the project; the header falls back to the id when unknown. */
  projectName?: string;
  onClose: () => void;
};
type Gen = "idle" | "building" | "ready" | "error";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const cookieName = (project: string) => `taiw-build-branch-${project}`;

function readBranchCookie(project: string): string {
  const m = document.cookie.match(new RegExp(`(?:^|; )${cookieName(project)}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : "";
}
function writeBranchCookie(project: string, branch: string): void {
  document.cookie = `${cookieName(project)}=${encodeURIComponent(branch)}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
}
function fmtSize(n: number): string {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

export default function BuildsPage({ project, projectName, onClose }: Props) {
  const [branches, setBranches] = useState<string[]>([]);
  const [branch, setBranch] = useState<string>(() => readBranchCookie(project));
  const [builds, setBuilds] = useState<BuildItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState<Gen>("idle");
  const [detail, setDetail] = useState("");
  const [last, setLast] = useState<{ filename: string; buildId: string } | null>(null);
  const [error, setError] = useState("");
  const runId = useRef(0);
  // Stops the generate poll loop from calling setState after the view switches
  // away (BuildsPage unmounts when the user taps Back mid-build).
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const refreshBuilds = useCallback(async () => {
    try { setBuilds(await listBuilds(project)); } catch (e) { setError((e as Error).message); }
  }, [project]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([fetchBranches(project), listBuilds(project)]).then(([b, l]) => {
      if (cancelled) return;
      if (b.status === "fulfilled") {
        // Keep the cookie branch even if the remote list has not refreshed yet.
        const cookieBranch = readBranchCookie(project);
        const merged = Array.from(new Set([...(cookieBranch ? [cookieBranch] : []), ...b.value]));
        setBranches(merged);
        setBranch((cur) => cur || cookieBranch || b.value[0] || "");
      } else {
        setError((b.reason as Error)?.message ?? "Failed to load branches");
      }
      if (l.status === "fulfilled") setBuilds(l.value);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [project]);

  const onSelectBranch = useCallback((v: string) => {
    setBranch(v);
    writeBranchCookie(project, v);
  }, [project]);

  const generate = useCallback(async () => {
    if (!branch) { setError("Pick a branch first."); return; }
    const my = ++runId.current;
    setError(""); setLast(null); setGen("building"); setDetail("Building on the server…");
    let jobId: string;
    try { jobId = await generateBuild(project, branch); }
    catch (e) { setError((e as Error).message); setGen("error"); return; }
    const start = Date.now();
    while (Date.now() - start < 300_000) {
      await sleep(2000);
      if (!alive.current || runId.current !== my) return;
      let st;
      try { st = await getBuildStatus(jobId); } catch { continue; }
      if (st.status === "ready") {
        setGen("ready");
        setDetail("");
        if (st.filename && st.buildId) setLast({ filename: st.filename, buildId: st.buildId });
        await refreshBuilds();
        return;
      }
      if (st.status === "error") { setError(st.error ?? "Build failed."); setGen("error"); return; }
      setDetail("Building on the server…");
    }
    if (alive.current) { setError("Build timed out."); setGen("error"); }
  }, [project, branch, refreshBuilds]);

  return (
    <div className="builds-page">
      <header className="builds-header">
        <button type="button" className="builds-back" onClick={onClose} aria-label="Back to chat">←</button>
        <h1>Builds · {projectName ?? project}</h1>
      </header>

      <section className="builds-generate">
        <label className="builds-field">
          <span>Branch</span>
          <input
            className="builds-branch-input"
            list="taiw-branch-options"
            value={branch}
            onChange={(e) => onSelectBranch(e.target.value)}
            disabled={gen === "building"}
            placeholder={loading ? "Loading branches…" : "Type to search a branch"}
            spellCheck={false}
            autoComplete="off"
          />
          <datalist id="taiw-branch-options">
            {branches.map((b) => <option key={b} value={b} />)}
          </datalist>
        </label>
        <button
          type="button"
          className="builds-generate-btn"
          onClick={() => void generate()}
          disabled={gen === "building" || !branch}
        >
          {gen === "building" ? "Building…" : "Generate build"}
        </button>
        {gen === "building" && detail && <span className="builds-hint">{detail}</span>}
        {gen === "ready" && last && (
          <a className="builds-download-cta" href={buildDownloadUrl(project, last.buildId)} target="_blank" rel="noopener">Download {last.filename}</a>
        )}
        {gen === "error" && error && <span className="builds-error" role="alert">{error}</span>}
      </section>

      <section className="builds-list">
        <h2>Past builds</h2>
        {loading && <p className="builds-hint">Loading…</p>}
        {!loading && builds.length === 0 && <p className="builds-hint">No builds yet. Generate one above.</p>}
        <ul>
          {builds.map((b) => (
            <li key={b.id} className="builds-row">
              <div className="builds-row-main">
                <span className="builds-filename">{b.filename}</span>
                <span className="builds-meta">
                  {b.version ? `commit ${b.version}` : ""}
                  {b.notes ? ` · ${b.notes}` : ""}
                  {` · ${fmtSize(b.sizeBytes)}`}
                  {b.uploadedByName ? ` · ${b.uploadedByName}` : ""}
                </span>
                {b.createdAt > 0 && (
                  <span className="builds-time">
                    <time dateTime={isoTime(b.createdAt)} title={absoluteTime(b.createdAt)}>
                      {absoluteTime(b.createdAt)}
                    </time>
                    <span className="builds-age"> · {relativeAge(b.createdAt)}</span>
                  </span>
                )}
              </div>
              <a className="builds-download" href={buildDownloadUrl(project, b.id)} target="_blank" rel="noopener">Download</a>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
